import { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import _ from 'lodash';
import * as ROUTES from '@/constants/routes';
import { AIRPORT_CODES, AIRPORT_CODES_BY_ZIP } from '@/constants/airportCodes';
import { clearImpersonation, setImpersonatedClientId, setImpersonatedOrgId } from '@/utilities/impersonation';
import { UserContext } from '../../App';
import { QUERY_USERS_BY_ROLE } from './queries';
import { UPDATE_CLIENT_BY_ID } from './mutations';
import { SEARCHABLE, TABS } from './constants';

const DEFAULT_TAB = TABS.find((t) => t.data === 'shippers');

/** Super Admin Users list state: the selected tab, its rows after the airport filter and search, and paging. */
export const useAdminUsers = () => {
    const { realUser } = useContext(UserContext);
    const [tab, setTab] = useState(DEFAULT_TAB);
    const [page, setPage] = useState(0);
    const [searchInput, setSearchInput] = useState('');
    const [selectedAirports, setSelectedAirports] = useState([]);

    // withAdminRights also admits corporate admins, so the org list is limited to Onward admins.
    const isOnwardAdmin = !!realUser?.roles?.['ONWARD_ADMIN'];
    const visibleTabs = useMemo(() => TABS.filter((t) => isOnwardAdmin || t.data !== 'orgAdmins'), [isOnwardAdmin]);

    const { loading, data } = useQuery(QUERY_USERS_BY_ROLE, {
        variables: { includeOrgAdmins: isOnwardAdmin },
        onError: (error) => {
            console.error(error);
            Sentry.captureException(error);
        },
    });

    const [updateClient, { loading: updateLoading }] = useMutation(UPDATE_CLIENT_BY_ID);

    const filteredUsers = useMemo(() => {
        const tabUsers = data?.[tab.data] || [];

        let tabUsersWithAirportCodes;

        if (tab.data === 'carriers') {
            tabUsersWithAirportCodes = tabUsers
                .map((tabUser) => {
                    const airportCodes = [];
                    tabUser.locations?.forEach((location) => {
                        const airportCode = AIRPORT_CODES_BY_ZIP[location.business_zip];
                        if (airportCode && !airportCodes.includes(airportCode)) {
                            airportCodes.push(airportCode);
                        }
                    });
                    return {
                        ...tabUser,
                        airportCodes,
                    };
                })
                .filter((tabUser) => {
                    if (selectedAirports.length === 0) return true;
                    return tabUser.airportCodes.some((code) => selectedAirports.includes(code));
                });
        } else if (tab.data === 'orgAdmins') {
            tabUsersWithAirportCodes = tabUsers.map((org) => ({
                ...org,
                adminEmails: org.users.map((u) => u.email).join(', '),
            }));
        } else {
            tabUsersWithAirportCodes = tabUsers;
        }

        if (searchInput && searchInput.length) {
            return tabUsersWithAirportCodes.filter((tabUser) =>
                SEARCHABLE.some((searchField) => {
                    const tabUserField = _.get(tabUser, searchField);
                    if (tabUserField && tabUserField.length) {
                        const fieldLower = tabUserField.toLowerCase();
                        const searchLower = searchInput.toLowerCase();
                        return fieldLower.includes(searchLower) || searchLower.includes(fieldLower);
                    }
                    return false;
                })
            );
        }

        return tabUsersWithAirportCodes;
    }, [data, tab, searchInput, selectedAirports]);

    const airportOptions = useMemo(() => {
        return AIRPORT_CODES.map((airport) => {
            return {
                label: airport,
                value: airport,
                selected: selectedAirports.includes(airport),
            };
        });
    }, [selectedAirports]);

    const changeTab = (...[, newTab]) => {
        const nextTab = TABS.find((t) => t.data === newTab);
        setTab(nextTab);
        setPage(0);
    };

    return {
        tab,
        changeTab,
        visibleTabs,
        page,
        setPage,
        searchInput,
        setSearchInput,
        setSelectedAirports,
        airportOptions,
        filteredUsers,
        loading,
        updateClient,
        updateLoading,
    };
};

/** Super Admin Users row actions: log in as a client, simulate an organization, or open a client's details. */
export const useAdminUserActions = () => {
    const { setClientId, setSimOrgId } = useContext(UserContext);
    const navigate = useNavigate();

    const logInAsUser = (entity) => {
        const { user_id, roles } = entity.user;

        if (roles?.['ONWARD_ADMIN']) {
            alert('Logging in as another admin is not allowed');
            return;
        }

        clearImpersonation();
        setSimOrgId(null);
        setImpersonatedClientId(user_id);
        setClientId(user_id);
        navigate('/account');
    };

    const logInAsOrg = (org) => {
        clearImpersonation();
        setClientId(null);
        setImpersonatedOrgId(org.organization_id);
        setSimOrgId(org.organization_id);
        navigate(ROUTES.CORPORATE_USERS);
    };

    const goToUser = (entity) => {
        if (entity.user.roles.SHIPPER) {
            navigate(`/admin/shipper/${entity.client_id}`);
        } else if (entity.user.roles.CARRIER || entity.user.roles.CONTRACTOR) {
            navigate(`/admin/carrier/${entity.client_id}`);
        }
    };

    return { logInAsUser, logInAsOrg, goToUser };
};
